import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

const STORAGE_KEY = 'kast_cookie_consent'

type ConsentValue = 'all' | 'necessary'

function readStoredConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'all' || v === 'necessary') return v
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
  return null
}

function writeConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // ignore quota / private-mode errors
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false)
  const acceptRef = useRef<HTMLButtonElement>(null)

  // Defer the localStorage read to after hydration so SSR-like setups don't flash
  useEffect(() => {
    if (readStoredConsent() === null) {
      setVisible(true)
    }
  }, [])

  // Move initial focus to the first button when the banner appears so keyboard
  // users are aware of it without being forced into a modal trap.
  useEffect(() => {
    if (visible) {
      acceptRef.current?.focus()
    }
  }, [visible])

  function handleConsent(value: ConsentValue) {
    writeConsent(value)
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'var(--bg-elevated, #1a1a2e)',
        borderTop: '1px solid var(--border-primary, #1e1e3a)',
        // Ensure the banner sits above page content but doesn't obscure essential
        // controls — fixed-bottom keeps it out of the reading flow.
        boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <p
          style={{
            flex: 1,
            minWidth: '200px',
            margin: 0,
            fontSize: '14px',
            color: 'var(--text-secondary, #9ca3af)',
            lineHeight: '1.6',
          }}
        >
          We use cookies to keep you signed in and save your preferences. No
          tracking or advertising cookies.{' '}
          <Link
            to="/privacy#cookies"
            style={{ color: 'var(--accent-light, #a78bfa)', textDecoration: 'underline' }}
          >
            Cookie policy
          </Link>
        </p>

        {/*
          Both buttons carry identical visual weight — same border-box size,
          same border treatment, same font. Only color distinguishes them.
          This satisfies the GDPR/EDPB requirement that "Reject all" is no
          harder to reach than "Accept all" (equal prominence, equal click
          count). Confirm-shaming language ("No thanks, I hate saving money")
          is explicitly avoided.
        */}
        <div
          style={{ display: 'flex', gap: '8px', flexShrink: 0 }}
          role="group"
          aria-label="Cookie consent options"
        >
          <button
            type="button"
            onClick={() => handleConsent('necessary')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid var(--border-primary, #1e1e3a)',
              backgroundColor: 'transparent',
              color: 'var(--text-primary, #ffffff)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              minWidth: '130px',
            }}
            className="cookie-consent-btn"
          >
            Necessary only
          </button>

          <button
            ref={acceptRef}
            type="button"
            onClick={() => handleConsent('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid var(--accent, #7c3aed)',
              backgroundColor: 'var(--accent, #7c3aed)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              minWidth: '130px',
            }}
            className="cookie-consent-btn"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  )
}
